// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import type { IntegrationListItem } from '@greenlight/shared';

import { BlockingGrantsNotice } from '@/components/integrations/BlockingGrantsNotice';
import {
  blockingGrantsFromError,
  type BlockingGrants,
} from '@/components/integrations/blockingGrants';

export interface DeleteIntegrationDialogProps {
  /** The integration being deleted, or null when closed. */
  integration: IntegrationListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Guarded soft-delete of a whole integration. Deleting deactivates the integration
 * and every credential it holds and destroys their vault secrets, but is refused
 * (409) while any app or user holds a pending/granted grant on it. On refusal the
 * dialog names the blocking apps and users and links to the grants view filtered to
 * this integration so IT revokes them one at a time (docs/09 § Integration deletion). The rows are never
 * hard-deleted, so revoked/denied grant history keeps a resolvable target and the
 * name stays reserved.
 */
export function DeleteIntegrationDialog({
  integration,
  open,
  onOpenChange,
}: DeleteIntegrationDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const deleteIntegration = api.integrations.deleteIntegration.useMutation();
  const [blocking, setBlocking] = React.useState<BlockingGrants | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setBlocking(null);
      setError(null);
    }
  }, [open]);

  const grantsHref =
    integration != null
      ? `/integrations?view=grants&group=integration&integrationId=${integration.id}`
      : '/integrations?view=grants';

  // The credentials (and their vault secrets) this delete destroys — named in the
  // confirm step so IT sees exactly what is torn down (docs/28 § /integrations).
  const credentials = integration?.credentials ?? [];

  async function handleDelete(): Promise<void> {
    if (!integration) return;
    setError(null);
    setBlocking(null);
    try {
      const res = await deleteIntegration.mutateAsync({ params: { id: integration.id } });
      if (res.status >= 300) throw res;
      toast.success(`Integration "${integration.name}" deleted.`);
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onOpenChange(false);
    } catch (err) {
      const refused = blockingGrantsFromError(err, 'integration.in_use');
      if (refused) {
        setBlocking(refused);
        return;
      }
      setError('Failed to delete the integration. Please try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-0 overflow-hidden sm:max-w-md">
        <DialogHeader className="min-w-0">
          <DialogTitle>Delete integration</DialogTitle>
          <DialogDescription className="break-words">
            {integration ? (
              <>
                Permanently delete {integration.name} and destroy the vault secret behind every
                credential it holds. The grant history is kept and the name stays reserved, but no
                app can be granted this integration again.
              </>
            ) : (
              ''
            )}
          </DialogDescription>
        </DialogHeader>

        {blocking ? (
          <BlockingGrantsNotice
            subject="integration"
            blocking={blocking}
            grantsHref={grantsHref}
            integrationName={integration?.name}
            data-testid="delete-integration-blocked"
          />
        ) : (
          <div className="space-y-2 text-[12.5px] leading-relaxed text-muted-foreground">
            <p>
              This can’t be undone. Deleting is blocked while any app or person holds a pending or
              granted grant on the integration.
            </p>
            {credentials.length > 0 && (
              <div data-testid="delete-integration-credentials">
                <p className="font-medium text-foreground">
                  {credentials.length === 1
                    ? '1 credential and its vault secret will be destroyed:'
                    : `${credentials.length} credentials and their vault secrets will be destroyed:`}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {credentials.map((c) => (
                    <li key={c.slug} className="flex flex-wrap items-center gap-1.5">
                      <MonoValue>{c.slug}</MonoValue>
                      <MonoValue>{c.vault_key}</MonoValue>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {error && <p className="text-[11.5px] text-destructive">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteIntegration.isPending || blocking !== null}
            onClick={() => void handleDelete()}
          >
            {deleteIntegration.isPending ? 'Deleting…' : 'Delete integration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
